// ABOUTME: Opens the full-screen region chooser and acts on the answer.
// ABOUTME: Fails open at every step — a gate nobody can get past is worse than no gate.

import { Component } from '@theme/component';
import { chosenRegion, recordChoice } from './region-persistence.js';
import { storage } from './region-storage.js';
import { isCrawler, gateDestination } from './region-gate-rules.js';

/**
 * @typedef {Object} RegionGateRefs
 * @property {HTMLDialogElement} dialog
 *
 * @extends {Component<RegionGateRefs>}
 */
class RegionGate extends Component {
  /** True once the visitor has picked a region. The only thing that may close the panel. */
  #answered = false;

  connectedCallback() {
    super.connectedCallback();

    // Nothing below is worth a broken store. Anything unexpected and the dialog
    // stays closed, which leaves the visitor on the page they asked for.
    try {
      this.open();
    } catch (error) {
      console.error(error);
    }
  }

  open() {
    // The inline script in region-gate.liquid paints the red ground at parse time
    // so the store does not flash up before this module resolves. Whatever is
    // decided below, that pre-paint comes down here: on the paths that open the
    // panel it is replaced by the real thing in the same frame, and on the paths
    // that do not, it must not be left covering a store nobody is gating.
    try {
      this.#show();
    } finally {
      document.documentElement.classList.remove('region-gate-priming');
    }
  }

  #show() {
    // No geolocation, deliberately: guessing is the thing customers are already
    // getting wrong for themselves, and /browsing_context_suggestions.json would
    // only put a slower guess in front of the same question.
    if (isCrawler(navigator.userAgent)) return;

    // Reads the shared cookie as well as this origin's own record, so a choice
    // made on one store is not asked for again on the store it sent them to.
    if (chosenRegion(storage)) return;

    const { dialog } = this.refs;
    if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;

    // There is no dismiss affordance, and Escape is one, so it is refused twice.
    //
    // Refusing `cancel` is the quiet way, but it only works once the visitor has
    // interacted with the page: the browser makes the event cancelable on sticky
    // activation and not before, so a gate that opens on arrival cannot rely on
    // it — the first Escape gets through. Verified in Chrome, not assumed.
    dialog.addEventListener('cancel', (event) => event.preventDefault());

    // So the panel is reopened if it closes without an answer. `#answered` rather
    // than a storage read: with storage blocked there is no answer to read back,
    // and reopening on that would be a locked door instead of a question.
    dialog.addEventListener('close', () => {
      if (this.#answered) return;
      try {
        dialog.showModal();
      } catch (error) {
        // A panel that will not reopen is not worth stranding anyone over.
        console.error(error);
        document.documentElement.classList.remove('region-gate-open');
      }
    });

    // showModal, not the open attribute: it is what makes the rest of the page
    // inert and keeps the keyboard inside the four buttons, with no focus trap of
    // our own to get wrong.
    dialog.showModal();

    document.documentElement.classList.add('region-gate-open');
  }

  /**
   * @param {Event} event - Delegated from the chosen button by `on:click`.
   */
  choose(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const region = target?.dataset?.region;
    if (!region) return;

    this.#answered = true;

    // Recording is never what keeps someone here: every write inside
    // region-persistence.js is already wrapped, so blocked storage costs the
    // visitor a repeat of this question on the next page, not a way out.
    recordChoice(storage, region);

    const destination = gateDestination(region, this.dataset.storeRegion, window.location.href);

    if (destination.action === 'redirect') {
      window.location.assign(destination.url);
      return;
    }

    // 'stay' means they picked the store they are already on, which is an answer,
    // not a no-op. Reusing resolveTarget's same-origin guard is what makes it one.
    this.close();
  }

  close() {
    const { dialog } = this.refs;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
    document.documentElement.classList.remove('region-gate-open');
  }
}

if (!customElements.get('region-gate')) {
  customElements.define('region-gate', RegionGate);
}
